import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useBookingModal } from '../contexts/BookingModalContext';

// Team photos for rotating background
const teamPhotos = [
  '/images/MSCG Team Footer_1.png',
  '/images/MSCG Team Footer_2.png',
  '/images/MSCG Team Footer_3.png',
  '/images/MSCG Team Footer_4.png',
  '/images/MSCG Team Footer_5.png',
];

export const Footer: React.FC = () => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const { openModal } = useBookingModal();
  const location = useLocation();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhotoIndex((prev) => (prev + 1) % teamPhotos.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <footer className="w-full">
      {/* Hero Section - Rotating photos with dark gradient overlay */}
      <div className="relative min-h-[50vh] overflow-hidden">
        {/* Rotating background images */}
        {teamPhotos.map((photo, index) => (
          <div
            key={photo}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentPhotoIndex ? 'opacity-100' : 'opacity-0'
              }`}
            style={{
              backgroundImage: `url('${photo}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'top center',
            }}
          />
        ))}

        {/* Dark gradient overlay - bottom (opacity 1) to top (opacity 0) */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, rgba(41, 48, 57, 1) 0%, rgba(41, 48, 57, 0.7) 50%, rgba(41, 48, 57, 0) 100%)',
          }}
        />

        {/* Content - matching header width */}
        <div className="relative z-10 h-full px-4 sm:px-6 lg:px-8 py-20 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 min-h-[50vh]">
          <h2 className="text-white text-4xl md:text-5xl lg:text-5xl font-serif leading-tight max-w-xl">
            You don't have to <span className="text-[#d68800]">figure out things</span> on your own
          </h2>

          {/* Book Now button - matching header style */}
          {location.pathname !== '/healing-within-reach' && location.pathname !== '/our-team' && (
            <button
              onClick={openModal}
              className="inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
              style={{ fontSize: '1rem' }}
            >
              Book Now
              <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Main Footer Links Section */}
      <div className="bg-mscg-dark-gray text-white">
        <div className="px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">

            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/images/Michelle Simmons Logo Resized.png"
                  alt="MSCG"
                  className="h-12 w-auto"
                  style={{ borderRadius: '60px' }}
                />
              </div>
              <p className="text-neutral-300 text-base leading-relaxed mb-6 font-medium">
                MSCG Your Healing and Reach
              </p>
              <p className="text-neutral-500 text-xs">
                © Michelle Simmons Counseling Group. Designed with ❤️ by <a href="https://creativeyadley.com" target="_blank" rel="noopener noreferrer" className="text-mscg-orange hover:text-white transition-colors">Creative Yadley</a>
              </p>
            </div>

            {/* Navigate Column */}
            <div className="border-l border-neutral-700 pl-8">
              <h4 className="text-xl font-semibold mb-6 text-neutral-300">Navigate</h4>
              <ul className="space-y-4 text-neutral-400 text-sm">
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Home</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/blog" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Blog</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/our-team" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Our Team</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/resources" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Resources</Link>
                </li>
              </ul>
            </div>

            {/* Get Help Column */}
            <div className="border-l border-neutral-700 pl-8">
              <h4 className="text-xl font-semibold mb-6 text-neutral-300">Get Help</h4>
              <ul className="space-y-4 text-neutral-400 text-sm">
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/contact" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Contact</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/resources" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Resources</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/services/drug-alcohol" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Drug & Alcohol Services</Link>
                </li>
                <li className="flex items-center gap-3 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-mscg-orange flex-shrink-0" />
                  <Link to="/services/outpatient" className="hover:text-white transition-all duration-300 group-hover:translate-x-1">Outpatient Therapy Services</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Large Animated Letters Section - Full Width */}
      <div className="relative bg-mscg-dark-gray overflow-hidden">
        <div
          className="w-full flex items-center justify-center py-8"
          style={{
            fontSize: 'clamp(2rem, 12vw, 12rem)',
            fontWeight: 900,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            fontFamily: '"Montserrat", "Sofia Pro", sans-serif',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          {'Michelle Simmons'.split('').map((letter, index) => (
            <span
              key={index}
              className="footer-letter"
              style={{
                display: 'inline-block',
                color: 'rgba(255, 255, 255, 0.08)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'default',
                transitionDelay: `${index * 0.02}s`,
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget;
                el.style.color = '#ffffff';
                el.style.transform = 'translateY(-8px) scale(1.05)';
                el.style.textShadow = '0 0 40px rgba(214, 136, 0, 0.6), 0 0 80px rgba(214, 136, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget;
                el.style.color = 'rgba(255, 255, 255, 0.08)';
                el.style.transform = 'translateY(0) scale(1)';
                el.style.textShadow = 'none';
              }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </span>
          ))}
        </div>
      </div>
    </footer>
  );
};

